/**
 * 再構築（染色体級）の定数。正本は
 * docs/systems/gene-analysis/Kensuke_GeneAnalysis_SPECIFICATION.md §0.1 / §0.2 / §0.3.4。
 */

/** 固定長チャンク（塩基数）。§0.1 の 64–256 kbp のうち上端を採る。 */
export const DEFAULT_CHUNK_SIZE = 262144;

/** 1 配列のハード上限（塩基）。ヒト chr1 ≈249 Mbp に余裕を持たせた §0.2 の値。 */
export const MAX_SEQUENCE_LENGTH = 300_000_000;

/** クリップボード貼り付けの実用上限（塩基）。染色体級はファイル取込が正（§0.1）。 */
export const MAX_PASTE_LENGTH = 10_000_000;

/** .gproj ZIP の上限（バイト）。§0.2 */
export const MAX_GPROJ_ZIP_BYTES = 512 * 1024 * 1024;

/** .gproj 展開後の上限（バイト）。§0.2 */
export const MAX_GPROJ_EXPANDED_BYTES = 1024 * 1024 * 1024;

/** ZIP エントリ数・配列本数・マニフェストの上限（旧実装から据え置き。§3.1） */
export const MAX_GPROJ_ENTRIES = 8192;
export const MAX_SEQUENCES_PER_PROJECT = 256;
export const MAX_MANIFEST_BYTES = 1024 * 1024;

/** プロジェクト名・配列名の最大長（§3.1） */
export const MAX_PROJECT_NAME_LENGTH = 255;
export const MAX_SEQUENCE_NAME_LENGTH = 64;

/** 常駐チャンクの既定上限（バイト）。1 byte/塩基なので 64 MB ≒ 64 Mbp 相当。 */
export const DEFAULT_RESIDENT_BYTES_LIMIT = 64 * 1024 * 1024;

/**
 * 塩基色（§0.3.4）。文字色として使う。背景のベタ塗りには使わない。
 * G は既定 `#CA8A04`（黄）。初期 Dye Set の黒は `GUANINE_COLOR_BLACK` に切替可。
 */
export const BASE_COLORS_LIGHT = {
  A: '#16A34A',
  C: '#2563EB',
  G: '#CA8A04',
  T: '#DC2626',
  N: '#9CA3AF',
} as const;

/**
 * 明背景の高コントラスト版（§0.3.3 の WCAG 2.1 AA を満たす）。
 * 色相（緑・青・黄・赤）は `BASE_COLORS_LIGHT` と同じで、A と G と N だけ暗くしてある。
 * 既定は仕様色。設定で切り替える。
 */
export const BASE_COLORS_LIGHT_AA = {
  A: '#15803D',
  C: '#2563EB',
  G: '#A16207',
  T: '#DC2626',
  N: '#6B7280',
} as const;

/** 暗背景テーマ。色相は変えず輝度だけ上げる（§0.3.4）。 */
export const BASE_COLORS_DARK = {
  A: '#4ADE80',
  C: '#60A5FA',
  G: '#FACC15',
  T: '#F87171',
  N: '#CBD5E1',
} as const;

/** G を初期 Dye Set の黒にするときの色（明背景 / 暗背景）。 */
export const GUANINE_COLOR_BLACK = '#171717';
export const GUANINE_COLOR_BLACK_DARK = '#E5E7EB';

export type BaseColorPalette = Record<'A' | 'C' | 'G' | 'T' | 'N', string>;
